import React, { useState } from 'react';
import CalculatorCard from '../ui/CalculatorCard';
import { Car, Fuel, Calculator, TrendingUp, MapPin, DollarSign, BarChart3, RotateCcw } from 'lucide-react';

interface FuelData {
  distance: number;
  fuelUsed: number;
  fuelPrice: number;
  vehicleType: string;
  drivingStyle: string;
}

interface FuelResults {
  consumption: number; // L/100km
  costPer100km: number;
  costPerKm: number;
  totalCost: number;
  efficiency: string;
  co2Emission: number;
  annualCost: number;
}

const FuelConsumptionCalculator: React.FC = () => {
  const [fuelData, setFuelData] = useState<FuelData>({
    distance: 0,
    fuelUsed: 0,
    fuelPrice: 0,
    vehicleType: 'sedan',
    drivingStyle: 'normal'
  });

  const [results, setResults] = useState<FuelResults>({
    consumption: 0,
    costPer100km: 0,
    costPerKm: 0,
    totalCost: 0,
    efficiency: '',
    co2Emission: 0,
    annualCost: 0
  });

  const [calculationMode, setCalculationMode] = useState('consumption'); // consumption, cost, planning

  // Araç türleri ve ortalama tüketim değerleri
  const vehicleTypes = [
    { id: 'sedan', name: 'Sedan', avgConsumption: 7.5 },
    { id: 'suv', name: 'SUV', avgConsumption: 9.5 },
    { id: 'hatchback', name: 'Hatchback', avgConsumption: 6.5 },
    { id: 'pickup', name: 'Pickup', avgConsumption: 11.0 },
    { id: 'minivan', name: 'Minivan', avgConsumption: 8.5 },
    { id: 'sports', name: 'Spor Araba', avgConsumption: 12.0 },
    { id: 'hybrid', name: 'Hibrit', avgConsumption: 4.5 },
    { id: 'electric', name: 'Elektrikli', avgConsumption: 0 }
  ];

  // Sürüş stilleri ve tüketim çarpanları
  const drivingStyles = [
    { id: 'eco', name: 'Ekonomik', multiplier: 0.85 },
    { id: 'normal', name: 'Normal', multiplier: 1.0 },
    { id: 'aggressive', name: 'Agresif', multiplier: 1.25 },
    { id: 'city', name: 'Şehir İçi', multiplier: 1.15 },
    { id: 'highway', name: 'Otoyol', multiplier: 0.90 }
  ];

  // Yakıt tüketimi hesaplama
  const calculateFuelConsumption = () => {
    if (fuelData.distance <= 0 || fuelData.fuelUsed <= 0) {
      alert('Lütfen geçerli mesafe ve yakıt miktarı girin!');
      return;
    }

    // L/100km hesaplama
    const consumption = (fuelData.fuelUsed / fuelData.distance) * 100;

    // Maliyet hesaplamaları
    const costPer100km = consumption * fuelData.fuelPrice;
    const costPerKm = costPer100km / 100;
    const totalCost = fuelData.fuelUsed * fuelData.fuelPrice;

    // CO2 emisyonu (yakıt türüne göre)
    const co2PerLiter = 2.31; // kg CO2 per liter gasoline
    const co2Emission = fuelData.fuelUsed * co2PerLiter;

    // Yıllık maliyet tahmini (15,000 km/yıl)
    const annualDistance = 15000;
    const annualCost = (annualDistance / 100) * costPer100km;

    // Verimlilik değerlendirmesi
    let efficiency = '';
    if (consumption < 6) efficiency = 'Çok Ekonomik';
    else if (consumption < 8) efficiency = 'Ekonomik';
    else if (consumption < 10) efficiency = 'Orta';
    else if (consumption < 12) efficiency = 'Yüksek';
    else efficiency = 'Çok Yüksek';

    setResults({
      consumption: Math.round(consumption * 100) / 100,
      costPer100km: Math.round(costPer100km * 100) / 100,
      costPerKm: Math.round(costPerKm * 1000) / 1000,
      totalCost: Math.round(totalCost * 100) / 100,
      efficiency,
      co2Emission: Math.round(co2Emission * 100) / 100,
      annualCost: Math.round(annualCost * 100) / 100
    });
  };

  // Maliyet hesaplama (mesafe ve tüketim bilinen)
  const calculateTripCost = () => {
    const selectedVehicle = vehicleTypes.find(v => v.id === fuelData.vehicleType);
    const selectedStyle = drivingStyles.find(s => s.id === fuelData.drivingStyle);

    if (!selectedVehicle || !selectedStyle || fuelData.distance <= 0) {
      alert('Lütfen tüm bilgileri doldurun!');
      return;
    }

    const estimatedConsumption = selectedVehicle.avgConsumption * selectedStyle.multiplier;
    const fuelNeeded = (fuelData.distance / 100) * estimatedConsumption;
    const totalCost = fuelNeeded * fuelData.fuelPrice;
    const costPerKm = totalCost / fuelData.distance;
    const co2Emission = fuelNeeded * 2.31;

    setResults({
      consumption: Math.round(estimatedConsumption * 100) / 100,
      costPer100km: Math.round(estimatedConsumption * fuelData.fuelPrice * 100) / 100,
      costPerKm: Math.round(costPerKm * 1000) / 1000,
      totalCost: Math.round(totalCost * 100) / 100,
      efficiency: estimatedConsumption < 8 ? 'Ekonomik' : estimatedConsumption < 10 ? 'Orta' : 'Yüksek',
      co2Emission: Math.round(co2Emission * 100) / 100,
      annualCost: Math.round((15000 / 100) * estimatedConsumption * fuelData.fuelPrice * 100) / 100
    });
  };

  // Input değişikliği
  const handleInputChange = (field: keyof FuelData, value: string | number) => {
    setFuelData(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? value : Math.max(0, value)
    }));
  };

  // Temizle
  const clearAll = () => {
    setFuelData({
      distance: 0,
      fuelUsed: 0,
      fuelPrice: 0,
      vehicleType: 'sedan',
      drivingStyle: 'normal'
    });
    setResults({
      consumption: 0,
      costPer100km: 0,
      costPerKm: 0,
      totalCost: 0,
      efficiency: '',
      co2Emission: 0,
      annualCost: 0
    });
  };

  // Örnek veri yükleme
  const loadSampleData = () => {
    setFuelData({
      distance: 500,
      fuelUsed: 35,
      fuelPrice: 32.50,
      vehicleType: 'sedan',
      drivingStyle: 'normal'
    });
  };

  return (
    <CalculatorCard title="Yakıt Tüketimi Hesaplama - Araç Yakıt Masrafı Hesaplayıcı">
      <div className="space-y-6">
        {/* Açıklama */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            Yakıt tüketimi hesaplama aracı ile aracınızın yakıt masrafını hesaplayın.
            Araç yakıt tüketimi hesaplayıcı ile seyahat maliyetinizi öğrenin.
          </p>
        </div>

        {/* Hesaplama Modu Seçimi */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-white flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Hesaplama Türü
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { id: 'consumption', name: 'Tüketim Hesaplama', icon: '⛽', desc: 'Gerçek tüketimi hesapla' },
              { id: 'cost', name: 'Maliyet Tahmini', icon: '💰', desc: 'Seyahat maliyeti tahmin et' },
              { id: 'planning', name: 'Seyahat Planlama', icon: '🗺️', desc: 'Yolculuk bütçesi planla' }
            ].map(mode => (
              <button
                key={mode.id}
                onClick={() => setCalculationMode(mode.id)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${calculationMode === mode.id
                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                    : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500'
                  }`}
              >
                <div className="text-2xl mb-2">{mode.icon}</div>
                <div className="font-medium">{mode.name}</div>
                <div className="text-xs text-gray-400">{mode.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Tüketim Hesaplama Modu */}
        {calculationMode === 'consumption' && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-white flex items-center gap-2">
              <Fuel className="w-5 h-5" />
              Yakıt Tüketimi Bilgileri
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Kat Edilen Mesafe (km)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={fuelData.distance}
                  onChange={(e) => handleInputChange('distance', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="500"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Kullanılan Yakıt (Litre)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={fuelData.fuelUsed}
                  onChange={(e) => handleInputChange('fuelUsed', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="35"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Yakıt Fiyatı (₺/Litre)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={fuelData.fuelPrice}
                  onChange={(e) => handleInputChange('fuelPrice', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="32.50"
                />
              </div>
            </div>
          </div>
        )}

        {/* Maliyet Tahmini Modu */}
        {(calculationMode === 'cost' || calculationMode === 'planning') && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-white flex items-center gap-2">
              <Car className="w-5 h-5" />
              Araç ve Seyahat Bilgileri
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Araç Türü
                </label>
                <select
                  value={fuelData.vehicleType}
                  onChange={(e) => handleInputChange('vehicleType', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  {vehicleTypes.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.name} (Ort. {vehicle.avgConsumption}L/100km)
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Sürüş Stili
                </label>
                <select
                  value={fuelData.drivingStyle}
                  onChange={(e) => handleInputChange('drivingStyle', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  {drivingStyles.map(style => (
                    <option key={style.id} value={style.id}>
                      {style.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Seyahat Mesafesi (km)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={fuelData.distance}
                  onChange={(e) => handleInputChange('distance', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="500"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Yakıt Fiyatı (₺/Litre)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={fuelData.fuelPrice}
                  onChange={(e) => handleInputChange('fuelPrice', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="32.50"
                />
              </div>
            </div>
          </div>
        )}

        {/* Kontrol Butonları */}
        <div className="flex flex-wrap gap-3 justify-center">
          <button
            onClick={calculationMode === 'consumption' ? calculateFuelConsumption : calculateTripCost}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all duration-200 shadow-lg transform hover:scale-105 font-medium"
          >
            <Calculator className="w-5 h-5" />
            Hesapla
          </button>

          <button
            onClick={loadSampleData}
            className="flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg transform hover:scale-105"
          >
            <BarChart3 className="w-4 h-4" />
            Örnek Yükle
          </button>

          <button
            onClick={clearAll}
            className="flex items-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 shadow-lg transform hover:scale-105"
          >
            <RotateCcw className="w-4 h-4" />
            Temizle
          </button>
        </div>

        {/* Sonuçlar */}
        {(results.consumption > 0 || results.totalCost > 0) && (
          <div className="space-y-6">
            {/* Ana Sonuçlar */}
            <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 border-2 border-green-500/50 rounded-xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <h2 className="text-xl font-bold text-green-400">⛽ Yakıt Tüketimi Sonuçları</h2>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Tüketim</div>
                  <div className="text-white text-xl font-mono font-bold">{results.consumption}</div>
                  <div className="text-gray-500 text-xs">L/100km</div>
                </div>

                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">100km Maliyeti</div>
                  <div className="text-green-400 text-xl font-mono font-bold">₺{results.costPer100km}</div>
                  <div className="text-gray-500 text-xs">100 kilometre</div>
                </div>

                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Km Başı Maliyet</div>
                  <div className="text-blue-400 text-xl font-mono font-bold">₺{results.costPerKm}</div>
                  <div className="text-gray-500 text-xs">kilometre başı</div>
                </div>

                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-gray-400 text-sm mb-1">Toplam Maliyet</div>
                  <div className="text-purple-400 text-xl font-mono font-bold">₺{results.totalCost}</div>
                  <div className="text-gray-500 text-xs">bu seyahat</div>
                </div>
              </div>
            </div>

            {/* Detaylı Analiz */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-2 border-purple-500/50 rounded-xl p-6 shadow-2xl">
              <h2 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                📊 Detaylı Analiz
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="font-medium text-white mb-2">Verimlilik Durumu</div>
                  <div className={`text-lg font-bold ${results.efficiency === 'Çok Ekonomik' ? 'text-green-400' :
                      results.efficiency === 'Ekonomik' ? 'text-blue-400' :
                        results.efficiency === 'Orta' ? 'text-yellow-400' :
                          'text-red-400'
                    }`}>
                    {results.efficiency}
                  </div>
                  <div className="text-sm text-gray-400">Tüketim seviyesi</div>
                </div>

                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="font-medium text-white mb-2">CO₂ Emisyonu</div>
                  <div className="text-lg font-bold text-orange-400">{results.co2Emission} kg</div>
                  <div className="text-sm text-gray-400">Karbon ayak izi</div>
                </div>

                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="font-medium text-white mb-2">Yıllık Tahmini</div>
                  <div className="text-lg font-bold text-cyan-400">₺{results.annualCost}</div>
                  <div className="text-sm text-gray-400">15.000 km/yıl</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SEO İçerik */}
        <div className="mt-8 space-y-6 text-gray-300">
          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Yakıt Tüketimi Hesaplama Nasıl Yapılır?</h2>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Kat ettiğiniz mesafeyi kilometre cinsinden girin</li>
              <li>Bu mesafede kullandığınız yakıt miktarını litre olarak yazın</li>
              <li>Güncel yakıt fiyatını litre başına girin</li>
              <li>Araç türünüz ve sürüş stilinizi seçin</li>
              <li>"Hesapla" butonuna tıklayarak sonuçları görün</li>
              <li>Detaylı analiz ve maliyet tahminlerini inceleyin</li>
            </ol>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Yakıt Tüketimi ve Araç Verimliliği</h2>
            <p className="text-sm mb-3">
              Yakıt tüketimi hesaplama aracı ile araç yakıt masrafınızı hesaplayın.
              Araç yakıt tüketimi hesaplayıcı ile seyahat maliyetinizi öğrenin ve yakıt tasarrufu yapın.
              Yakıt masrafı hesaplama ile bütçenizi planlayın.
            </p>
            <div className="bg-gray-800 p-3 rounded text-sm">
              <strong>Tüketim Kategorileri:</strong> Çok Ekonomik (&lt;6L), Ekonomik (6-8L), Orta (8-10L), Yüksek (10-12L), Çok Yüksek (&gt;12L)<br />
              <strong>Hesaplama Formülü:</strong> (Kullanılan Yakıt ÷ Mesafe) × 100 = L/100km<br />
              <strong>Maliyet Hesabı:</strong> Tüketim × Yakıt Fiyatı = 100km Maliyeti
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Yakıt Tasarrufu İpuçları</h2>
            <div className="space-y-3 text-sm">
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Sürüş Teknikleri</h2>
                <p>Yakıt tasarrufu için etkili sürüş yöntemleri:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Sabit Hız:</strong> Ani hızlanma ve frenleme yapmayın<br />
                  <strong>Ekonomik Hız:</strong> 90-110 km/h arası optimal hız<br />
                  <strong>Motor Devresi:</strong> Düşük devirde sürüş yapın<br />
                  <strong>Klima Kullanımı:</strong> Yüksek hızda klima, düşük hızda cam açık
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Araç Bakımı</h2>
                <p>Düzenli bakım ile yakıt tasarrufu:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Lastik Basıncı:</strong> Düzenli kontrol ve ayarlama<br />
                  <strong>Motor Yağı:</strong> Kaliteli ve uygun viskozitede yağ<br />
                  <strong>Hava Filtresi:</strong> Temiz hava filtresi kullanımı<br />
                  <strong>Yakıt Sistemi:</strong> Periyodik temizlik ve bakım
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Seyahat Planlaması</h2>
                <p>Akıllı seyahat ile maliyet kontrolü:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><strong>Rota Optimizasyonu:</strong> En kısa ve verimli güzergah</li>
                  <li><strong>Trafik Saatleri:</strong> Yoğun saatleri kaçınma</li>
                  <li><strong>Araç Paylaşımı:</strong> Ortak seyahatler</li>
                  <li><strong>Toplu Taşıma:</strong> Alternatif ulaşım seçenekleri</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Araç Türlerine Göre Yakıt Tüketimi</h2>
            <div className="space-y-3 text-sm">
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Binek Araçlar</h2>
                <p>Farklı araç kategorilerinin tüketim özellikleri:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Sedan:</strong> 6-9 L/100km (orta boy aile arabası)<br />
                  <strong>Hatchback:</strong> 5-7 L/100km (şehir içi ideal)<br />
                  <strong>SUV:</strong> 8-12 L/100km (yüksek tüketim)<br />
                  <strong>Spor Araba:</strong> 10-15 L/100km (performans odaklı)
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Alternatif Yakıtlar</h2>
                <p>Çevre dostu araç seçenekleri:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Hibrit:</strong> 3-6 L/100km (elektrik + benzin)<br />
                  <strong>Elektrikli:</strong> 15-25 kWh/100km (sıfır emisyon)<br />
                  <strong>LPG:</strong> %40 daha ekonomik (benzin alternatifi)<br />
                  <strong>Dizel:</strong> %20 daha verimli (uzun mesafe)
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Ticari Araçlar</h2>
                <p>İş amaçlı araç tüketim analizi:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><strong>Pickup:</strong> 9-13 L/100km (yük taşıma)</li>
                  <li><strong>Minivan:</strong> 7-10 L/100km (yolcu taşıma)</li>
                  <li><strong>Kamyonet:</strong> 12-18 L/100km (ticari kullanım)</li>
                  <li><strong>Motosiklet:</strong> 3-5 L/100km (ekonomik ulaşım)</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Yakıt Fiyatları ve Piyasa Analizi</h2>
            <div className="space-y-3 text-sm">
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Türkiye Yakıt Piyasası</h2>
                <p>Güncel yakıt fiyatları ve trend analizi:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Benzin:</strong> 30-35 ₺/L (oktan değerine göre)<br />
                  <strong>Dizel:</strong> 32-37 ₺/L (ticari kullanım)<br />
                  <strong>LPG:</strong> 15-20 ₺/L (ekonomik seçenek)<br />
                  <strong>Elektrik:</strong> 2-4 ₺/kWh (şarj istasyonu)
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Maliyet Optimizasyonu</h2>
                <p>Yakıt masraflarını azaltma stratejileri:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Fiyat Takibi:</strong> Günlük fiyat karşılaştırması<br />
                  <strong>İndirim Kampanyaları:</strong> Akaryakıt firması promosyonları<br />
                  <strong> Kartı:</strong> Yakıt indirimi veren kartlar<br />
                  <strong>Toplu Alım:</strong> Ticari araçlar için anlaşmalar
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Gelecek Trendleri</h2>
                <p>Yakıt sektöründe beklenen gelişmeler:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><strong>Elektrikli Araçlar:</strong> Artan popülerlik ve altyapı</li>
                  <li><strong>Hibrit Teknoloji:</strong> Geçiş dönemi çözümleri</li>
                  <li><strong>Biyoyakıt:</strong> Çevre dostu alternatifler</li>
                  <li><strong>Hidrojen:</strong> Gelecek nesil yakıt teknolojisi</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Çevresel Etki ve Sürdürülebilirlik</h2>
            <div className="space-y-3 text-sm">
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Karbon Ayak İzi</h2>
                <p>Yakıt tüketiminin çevresel etkisi:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>CO₂ Emisyonu:</strong> 1 litre benzin = 2.31 kg CO₂<br />
                  <strong>Hava Kalitesi:</strong> Şehir içi kirlilik etkisi<br />
                  <strong>Küresel Isınma:</strong> Sera gazı salınımı<br />
                  <strong>Ekolojik Denge:</strong> Doğal yaşam alanları
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Sürdürülebilir Ulaşım</h2>
                <p>Çevre dostu ulaşım alternatifleri:</p>
                <div className="bg-gray-800 p-2 rounded mt-1">
                  <strong>Toplu Taşıma:</strong> Otobüs, metro, tramvay kullanımı<br />
                  <strong>Bisiklet:</strong> Kısa mesafe çevre dostu ulaşım<br />
                  <strong>Yürüyüş:</strong> Sağlıklı ve emisyonsuz alternatif<br />
                  <strong>Uzaktan Çalışma:</strong> Seyahat ihtiyacını azaltma
                </div>
              </div>
              <div>
                <h2 className="font-medium text-cyan-400 mb-1">Yeşil Teknolojiler</h2>
                <p>Gelişen çevre dostu araç teknolojileri:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><strong>Elektrikli Araçlar:</strong> Sıfır emisyon teknolojisi</li>
                  <li><strong>Güneş Enerjisi:</strong> Yenilenebilir enerji kaynağı</li>
                  <li><strong>Akıllı Şehirler:</strong> Entegre ulaşım sistemleri</li>
                  <li><strong>Otonom Araçlar:</strong> Verimli sürüş optimizasyonu</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">Sık Sorulan Sorular</h2>
            <div className="space-y-4 text-sm">
              <div>
                <h2 className="font-medium text-white mb-1">Yakıt tüketimi nasıl hesaplanır?</h2>
                <p>Kullanılan yakıt miktarını kat edilen mesafeye bölüp 100 ile çarparak L/100km değeri bulunur.</p>
              </div>
              <div>
                <h2 className="font-medium text-white mb-1">Hangi faktörler yakıt tüketimini etkiler?</h2>
                <p>Araç türü, motor hacmi, sürüş stili, trafik durumu, hava koşulları ve araç bakım durumu etkilidir.</p>
              </div>
              <div>
                <h2 className="font-medium text-white mb-1">Ekonomik sürüş nasıl yapılır?</h2>
                <p>Sabit hız, düşük motor devresi, ani hızlanma/frenleme yapmama ve düzenli bakım önemlidir.</p>
              </div>
              <div>
                <h2 className="font-medium text-white mb-1">Hibrit araçlar ne kadar tasarruf sağlar?</h2>
                <p>Hibrit araçlar geleneksel araçlara göre %30-50 oranında yakıt tasarrufu sağlayabilir.</p>
              </div>
              <div>
                <h2 className="font-medium text-white mb-1">Elektrikli araç maliyeti nasıl hesaplanır?</h2>
                <p>Elektrikli araçlarda kWh/100km tüketimi ile elektrik birim fiyatı çarpılarak maliyet bulunur.</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3 text-white">İlgili Araçlar</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <a href="/yolda-gecen-zaman-hesaplama" className="block p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors">
                <div className="font-medium text-cyan-400">Yolda Geçen Zaman</div>
                <div className="text-gray-400">Seyahat süresi hesaplama</div>
              </a>
              <a href="/elektrik-faturasi-hesaplama" className="block p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors">
                <div className="font-medium text-cyan-400">Elektrik Faturası</div>
                <div className="text-gray-400">Enerji maliyeti hesaplama</div>
              </a>
              <a href="/dogalgaz-faturasi-hesaplama" className="block p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors">
                <div className="font-medium text-cyan-400">Doğalgaz Faturası</div>
                <div className="text-gray-400">Gaz tüketimi hesaplama</div>
              </a>
              <a href="/zam-hesaplama" className="block p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors">
                <div className="font-medium text-cyan-400">Zam Hesaplama</div>
                <div className="text-gray-400">Fiyat artışı hesaplayıcı</div>
              </a>
              <a href="/kripto-kar-zarar-hesaplama" className="block p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors">
                <div className="font-medium text-cyan-400">Kripto Kar/Zarar</div>
                <div className="text-gray-400">Yatırım analizi</div>
              </a>
              <a href="/vize-final-ortalama-hesaplama" className="block p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors">
                <div className="font-medium text-cyan-400">Not Ortalaması</div>
                <div className="text-gray-400">Akademik başarı hesaplama</div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </CalculatorCard>
  );
};

export default FuelConsumptionCalculator;